import { createPartialApiContext, syncDownload, syncUpload, type SyncResult } from '@ms-cloudpack/api-server';
import { environmentInfo } from '@ms-cloudpack/environment';
import { red } from '@ms-cloudpack/task-reporter';
import {
  ExitCodeAuthenticationError,
  ExitCodeGenericError,
  type CommandAction,
  type CommandExitParams,
} from '../../types/CommandAction.js';
import { runPrerequisites } from '../../utilities/runPrerequisites.js';
import type { SyncOptions } from './types/SyncOptions.js';

function checkErrorCode(error: unknown, code: string): boolean {
  return error instanceof Error && 'code' in error && error.code === code;
}

export const execute: CommandAction<SyncOptions> = async (params) => {
  const { options, config, telemetryClient } = params;
  const { login, upload } = options;

  try {
    // Await here to ensure that try-catch block catches any errors thrown inside the async function
    return await telemetryClient.tracer.startActiveSpan('SYNC_EXECUTE', async (span): Promise<CommandExitParams> => {
      if (!config.features?.syncBundles) {
        return { message: 'Sync is disabled' };
      }

      if (!environmentInfo.cloudpack.isSyncEnabled) {
        span.setAttribute('syncDisabled', true);
        return { message: 'Sync is disabled by the user (CLOUDPACK_SYNC environment variable is set).' };
      }

      const remoteCacheConfig = config.remoteCache;
      if (!remoteCacheConfig) {
        return {
          message:
            'Remote cache is not configured. Please add remoteCache to your cloudpack.config.json file to use this feature.',
        };
      }

      const ctx = await createPartialApiContext({
        ...params,
        remoteCacheClientLoginMethod: login,
      });

      let syncResult: SyncResult;
      if (upload) {
        await runPrerequisites(params);

        syncResult = await syncUpload({ login }, ctx);
      } else {
        syncResult = await syncDownload(undefined, ctx);
      }

      return {
        message:
          `Sync completed with ${syncResult.success} successes, ${syncResult.failed} failures, ` +
          `and ${syncResult['already-exist'] + syncResult['not-found']} items that did not require syncing.`,
      };
    });
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AuthenticationRequiredError' && error.message.includes('CrossPlatformLockError')) {
        return {
          message: red(
            `Cloudpack encountered an issue acquiring the authentication lock. It seems "cloudpack sync" may already ` +
              `be running in another process. Please wait for the other process to complete and then try again.`,
          ),
          exitCode: ExitCodeGenericError,
        };
      }
      if (
        error.name === 'AuthenticationRequiredError' ||
        ('statusCode' in error && (error.statusCode === '401' || error.statusCode === '403'))
      ) {
        return {
          message: red('Authentication error. Please check your credentials and try again.'),
          exitCode: ExitCodeAuthenticationError,
        };
      }

      if (checkErrorCode(error, 'REQUEST_SEND_ERROR')) {
        return {
          message: red('Connection error. Please check your connection and try again.'),
          exitCode: ExitCodeGenericError,
        };
      }
    }

    throw error;
  }
};
